/**
 * Where this device becomes somebody's device.
 *
 * ── The token path (D-005) ──────────────────────────────────────────────────
 * `claimTokenFromLocation()` (called in src/main.tsx before anything renders)
 * has already taken any token out of the URL, written it to this device, and
 * rewritten the address bar to `#/access` — so by the time this screen exists
 * the credential is no longer in the URL, not in history, and not in a
 * screenshot of the address bar.
 *
 * What is left for this screen is the part that can fail: exchanging the token
 * for an identity through `GET /api/me`, caching it so the app knows who is
 * holding the phone with no signal, and saying something true about the result.
 *
 * The three outcomes are not interchangeable:
 *
 *   The server refused it → the token is DELETED. A stored token the server has
 *     rejected would fail every later request quietly, and the surveyor would
 *     experience it as "the app stopped working" rather than as "my link expired".
 *   No connection      → the token is KEPT, untouched. A basement is not a
 *     rejection, and throwing the link away there would be the worst possible
 *     bug in this file.
 *   It worked          → identity cached, a drain kicked, and one big button out.
 *
 * ── The UNLINKED path is now two paths (D-019) ──────────────────────────────
 * iOS gives a Home Screen web app a storage container separate from Safari's,
 * and that fact splits this screen's fourth outcome in half:
 *
 *   INSTALLED and unlinked — this is the app the surveyor works in, and until
 *     D-019 it could not be linked at all: every route in used to require the
 *     app to have been OPENED by a link, and a link tapped in Messages opens in
 *     Safari. So it gets the pairing path, front and centre.
 *   IN A BROWSER and unlinked — linking here would credential the container the
 *     surveyor is about to walk away from, and any work done here would be
 *     invisible from the installed icon. That is the trap that read as data
 *     loss on 2026-08-06. So this one gets ONE instruction: install first.
 *
 * A LINKED device gets neither, on either side of the wall. Nagging a phone
 * that is working is how a warning stops being read.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { PairDevice } from '../components';
import { hrefFor } from '../routing';
import { useServices } from '../state/services';
import { accessView, isRejection, type AccessPhase } from '../state/access';
import { isAppleTouchDevice, isStandalone } from '../state/displayMode';
import {
  clearToken,
  readCachedIdentity,
  readToken,
  writeCachedIdentity,
  type Identity,
} from '../state/identity';

export function AccessScreen(): ReactNode {

  /*
   * Read ONCE, at mount, into state that never changes.
   *
   * `display-mode` cannot change without a fresh page load — the app is either
   * launched from the icon or it is not — so re-reading it on every render
   * would buy nothing and would make the screen's copy depend on render timing.
   */
  const [standalone] = useState(isStandalone);
  const [apple] = useState(isAppleTouchDevice);

  const { api, sync } = useServices();
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<AccessPhase>(() =>
    readToken() ? 'checking' : 'unlinked',
  );
  // Seeded from the cache: a surveyor reopening the app in a pit has an identity
  // before any request is made, and that is what "offline but linked" rests on.
  const [identity, setIdentity] = useState<Identity | null>(() => readCachedIdentity());

  useEffect(() => {
    if (!readToken()) {
      setPhase('unlinked');
      return;
    }
    setPhase('checking');

    let cancelled = false;
    void api
      .me()
      .then((fresh) => {
        if (cancelled) return;
        writeCachedIdentity(fresh);
        setIdentity(fresh);
        setPhase('linked');
        // Assigned surveys are waiting server-side; pull them NOW rather than at
        // the next 30-second tick, so the list is populated when they tap
        // through. This screen exists because the link was just opened, which is
        // the one moment we know for certain there is both a token and a signal.
        //
        // It used to call `drain()` here, which pushes and cannot pull: the
        // comment promised assignments and the code delivered none, and the
        // "Assigned to you" list stayed empty forever.
        void sync
          .pullAssignments()
          .catch(() => undefined)
          .then(() => sync.drain())
          .catch(() => undefined);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isRejection(error)) {
          clearToken();
          setIdentity(null);
          setPhase('rejected');
          return;
        }
        setPhase('offline');
      });

    return () => {
      cancelled = true;
    };
  }, [api, sync, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const view = accessView({ phase, identity, standalone });

  return (
    <>
      {/* aria-live: the phase changes after an async call, so a screen-reader
          user is told the outcome instead of being left on "Setting up…". */}
      <div className="card" role="status" aria-live="polite">
        <p className="q" data-legibility="question">{view.headline}</p>
        <p className="ruleline" data-legibility="helper">{view.detail}</p>
      </div>

      {view.offer === 'pair' ? <PairDevice /> : null}

      {view.offer === 'install-first' ? <InstallFirst apple={apple} /> : null}

      {view.phase === 'rejected' ? (
        <p className="empty">
          Nothing you have already captured on this device is lost. It stays here until a new
          link arrives.
        </p>
      ) : null}

      <div className="navbar">
        {view.canProceed ? (
          <a className="btn btn--primary" href={hrefFor({ name: 'home' })}>
            My surveys
          </a>
        ) : null}

        {view.phase === 'offline' && !view.canProceed ? (
          <button type="button" className="btn btn--ghost" onClick={retry}>
            Try again
          </button>
        ) : null}

        {/* The way out, on both unusable states. It is a GHOST button under
         * the pairing panel or the install steps, never a competing primary:
         * the one obvious next action is still the one above it. But a device
         * that cannot be linked here may already be holding a day's work, and
         * a screen with no exit would put that work behind a wall. */}
        {view.phase === 'rejected' || view.phase === 'unlinked' ? (
          <a className="btn btn--ghost" href={hrefFor({ name: 'home' })}>
            My surveys
          </a>
        ) : null}
      </div>
    </>
  );
}

/**
 * The install-first instruction, for a browser tab that is not linked (D-019).
 *
 * ── Why there is no pairing form on this screen ─────────────────────────────
 * Pairing here would work, and that is the problem. It would credential the
 * BROWSER's storage container — a different IndexedDB, a different localStorage
 * — and the moment the surveyor added the icon they would open an app that had
 * never heard of them, with none of the work they had just done in it. That is
 * what happened on 2026-08-06 and it read as data loss. One instruction, and it
 * is the one that has to happen first.
 *
 * ── Numbered, and named for the phone in their hand ─────────────────────────
 * "Share, then Add to Home Screen" is exact on an iPhone and wrong on anything
 * else, so the steps are chosen by `isAppleTouchDevice()`. The DECISION to show
 * the warning is never platform-gated — a wrong guess about the phone must not
 * be able to suppress the warning on the phone that needs it.
 */
function InstallFirst({ apple }: { apple: boolean }): ReactNode {
  const steps = apple
    ? [
        'Tap the Share button at the bottom of the screen — the square with the arrow coming out of it.',
        'Scroll down and tap “Add to Home Screen”, then tap “Add”.',
        'Close this window and open UpSurv from the new icon.',
        'Ask the office for the setup code and scan it in there.',
      ]
    : [
        'Open your browser’s menu and choose “Install app” or “Add to Home screen”.',
        'Close this window and open UpSurv from the new icon.',
        'Ask the office for the setup code and scan it in there.',
      ];

  return (
    <div className="card">
      {/* Above the list, because it is why the list matters (D-011 item 6). */}
      <p className="leadin" data-legibility="helper">
        The app on your Home Screen and this browser window keep separate copies of everything.
        Nothing you save here will show up there.
      </p>
      <ol className="steps">
        {steps.map((step) => (
          <li key={step} className="steps__item" data-legibility="body">
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
}
